import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { ApiError } from "@/lib/apiClient";
import { planCoordsFromClient, renderPlanSvg, type PlanMarkerInput } from "@/lib/planViewer";
import { saveMaintenancePlanPoint } from "@/lib/wartungen";
import {
  PLAN_ZOOM_MAX,
  PLAN_ZOOM_MIN,
  PLAN_ZOOM_STEP,
  buildPlanLabelText,
  buildRenderedPlanImageUrl,
  clampMarkerOverlayScale,
  denormalizePlanPoint,
  getAssetDefaultLabelText,
  normalizePlanPointRecord,
  pointValue,
  type HuelleAsset,
  type MaintenancePlanRecord,
  type PlanPoint,
} from "./planModel";
import "./huellePlan.css";

type PlanEditMode = "display" | "object" | "label";

export interface HuellePlanViewerProps {
  plans: MaintenancePlanRecord[];
  activePlanId: number | null;
  onActivePlanIdChange: (planId: number | null) => void;
  selectedSubLocationId: number | null;
  assets: HuelleAsset[];
  filteredAssets: HuelleAsset[];
  selectedAssetId: number | null;
  onSelectAsset: (assetId: number | null) => void;
  onAssetPlanUpdated: (assetId: number, point: PlanPoint, plan: MaintenancePlanRecord) => void;
  onFeedback: (message: string, tone: "info" | "success" | "warning" | "error") => void;
}

export function HuellePlanViewer({
  plans,
  activePlanId,
  onActivePlanIdChange,
  selectedSubLocationId,
  assets,
  filteredAssets,
  selectedAssetId,
  onSelectAsset,
  onAssetPlanUpdated,
  onFeedback,
}: HuellePlanViewerProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const imageRef = useRef<SVGImageElement>(null);
  const layerRef = useRef<SVGGElement>(null);
  const metaRef = useRef<HTMLSpanElement>(null);
  const hintRef = useRef<HTMLParagraphElement>(null);
  const canvasWrapRef = useRef<HTMLDivElement>(null);

  const [activePageNumber, setActivePageNumber] = useState(1);
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  const [editMode, setEditMode] = useState<PlanEditMode>("display");
  const [planZoom, setPlanZoom] = useState(1);
  const [assetSearch, setAssetSearch] = useState("");
  const [labelText, setLabelText] = useState("");
  const [imageMetrics, setImageMetrics] = useState({ href: "", width: 1, height: 1 });
  const [saveInFlight, setSaveInFlight] = useState(false);

  const dragRef = useRef<{ pointerId: number; kind: "object" | "label" } | null>(null);
  const panRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startScrollLeft: number;
    startScrollTop: number;
    moved: boolean;
  } | null>(null);
  const suppressClickUntilRef = useRef(0);

  const activePlan = useMemo(
    () => plans.find((plan) => Number(plan.id) === Number(activePlanId)) || null,
    [plans, activePlanId],
  );

  const assetOptions = useMemo(() => {
    const search = assetSearch.trim().toLowerCase();
    return filteredAssets.filter((asset) => {
      if (!search) return true;
      const text = `${asset.external_id || ""} ${asset.name || ""} ${asset.location_label || ""}`.toLowerCase();
      return text.includes(search);
    });
  }, [filteredAssets, assetSearch]);

  const selectedAsset = useMemo(
    () => assets.find((asset) => Number(asset.asset_id) === Number(selectedAssetId)) || null,
    [assets, selectedAssetId],
  );

  const pageOptions = useMemo(() => {
    if (!activePlan) return [1];
    const known = new Set<number>([Number(activePlan.page_number) || 1, activePageNumber]);
    for (const point of activePlan.points || []) {
      const page = Number(point.page_number);
      if (page > 0) known.add(page);
    }
    const maxPage = Math.max(...known);
    return Array.from({ length: maxPage }, (_, i) => i + 1);
  }, [activePlan, activePageNumber]);

  useEffect(() => {
    if (!activePlan) return;
    setActivePageNumber(Number(activePlan.page_number || 1) || 1);
  }, [activePlan?.id]);

  const getSelectedPlanPoint = useCallback(
    (plan: MaintenancePlanRecord | null, assetId: number | null): PlanPoint | null => {
      if (!plan || !assetId) return null;
      const point = (plan.points || []).find((entry) => Number(entry.asset_id) === Number(assetId)) || null;
      if (point) return point;
      const asset = assets.find((entry) => Number(entry.asset_id) === Number(assetId));
      const position = (asset?.plan_positions || []).find((entry) => Number(entry.plan_id) === Number(plan.id));
      if (!position) return null;
      return normalizePlanPointRecord(
        {
          asset_id: assetId,
          x: position.plan_point?.x ?? null,
          y: position.plan_point?.y ?? null,
          label_x: position.plan_point?.label_x ?? position.label_position?.x ?? null,
          label_y: position.plan_point?.label_y ?? position.label_position?.y ?? null,
          label_text: position.label_text || "",
          page_number: position.plan_page || plan.page_number || 1,
        },
        plan,
      );
    },
    [assets],
  );

  const selectedPoint = getSelectedPlanPoint(activePlan, selectedAssetId);

  useEffect(() => {
    const defaultLabel = getAssetDefaultLabelText(selectedAsset);
    setLabelText(String(selectedPoint?.label_text || defaultLabel || "").trim());
  }, [selectedAsset, selectedPoint]);

  const ensureImageMetrics = useCallback(async (plan: MaintenancePlanRecord, pageNumber: number) => {
    const href = buildRenderedPlanImageUrl(plan, pageNumber) || plan.resolved_plan_image_url || "";
    if (!href) {
      setImageMetrics({ href: "", width: 1, height: 1 });
      return;
    }
    const metrics = await new Promise<{ href: string; width: number; height: number }>((resolve) => {
      const img = new Image();
      img.onload = () =>
        resolve({
          href,
          width: Math.max(1, img.naturalWidth || 1),
          height: Math.max(1, img.naturalHeight || 1),
        });
      img.onerror = () => resolve({ href, width: 1, height: 1 });
      img.src = href;
    });
    setImageMetrics(metrics);
  }, []);

  useEffect(() => {
    if (!activePlan) {
      setImageMetrics({ href: "", width: 1, height: 1 });
      return;
    }
    void ensureImageMetrics(activePlan, activePageNumber);
  }, [activePlan, activePageNumber, ensureImageMetrics]);

  const updateCanvasMetrics = useCallback(() => {
    const svg = svgRef.current;
    const wrap = canvasWrapRef.current;
    if (!svg || !wrap) return;
    const imageWidth = Math.max(1, imageMetrics.width);
    const imageHeight = Math.max(1, imageMetrics.height);
    const containerWidth = Math.max(1, wrap.clientWidth);
    const containerHeight = Math.max(1, wrap.clientHeight);
    const fitScale = Math.min(containerWidth / imageWidth, containerHeight / imageHeight);
    const effectiveScale = Math.max(fitScale * planZoom, 0.0001);
    const scaledWidth = imageWidth * effectiveScale;
    const scaledHeight = imageHeight * effectiveScale;
    const isZoomedBeyondFit = planZoom > 1.001;
    // Explizite Pixelgröße + flex-shrink:0, sonst staucht Flex das SVG auf Containerbreite.
    svg.style.width = `${scaledWidth}px`;
    svg.style.height = `${scaledHeight}px`;
    svg.style.flexShrink = "0";
    svg.style.maxWidth = isZoomedBeyondFit ? "none" : "100%";
    svg.style.maxHeight = isZoomedBeyondFit ? "none" : "100%";
    wrap.dataset.planZoomState = isZoomedBeyondFit ? "zoomed" : "fit";
  }, [imageMetrics.height, imageMetrics.width, planZoom]);

  const renderOverlay = useCallback(() => {
    const svg = svgRef.current;
    const image = imageRef.current;
    const layer = layerRef.current;
    if (!svg || !image || !layer) return;

    if (!activePlan) {
      renderPlanSvg({
        svg,
        image,
        layer,
        meta: metaRef.current,
        hint: hintRef.current,
        plan: null,
        emptyHintText: selectedSubLocationId
          ? "Für diesen Unterort ist noch kein Plan hinterlegt."
          : "Bitte einen Unterort auswählen.",
      });
      return;
    }

    const imageWidth = Math.max(1, imageMetrics.width);
    const imageHeight = Math.max(1, imageMetrics.height);
    const imageHref = buildRenderedPlanImageUrl(activePlan, activePageNumber) || activePlan.resolved_plan_image_url || "";
    const markerScale = clampMarkerOverlayScale(activePlan.marker_overlay_scale);
    const visiblePoints = filteredAssets
      .map((asset) => getSelectedPlanPoint(activePlan, asset.asset_id ?? null))
      .filter(Boolean) as PlanPoint[];
    const pagePoints = visiblePoints.filter(
      (point) => Number(point.page_number || activePlan.page_number || 1) === activePageNumber,
    );

    const markers: PlanMarkerInput[] = pagePoints.map((point) => {
      const asset = assets.find((entry) => Number(entry.asset_id) === Number(point.asset_id));
      const anchor = denormalizePlanPoint(pointValue(point, "object"), imageWidth, imageHeight);
      const labelPosition = denormalizePlanPoint(pointValue(point, "label"), imageWidth, imageHeight);
      const isSelected = Number(point.asset_id) === Number(selectedAssetId);
      const label = buildPlanLabelText(point, asset);
      const sym = asset?.plan_label_symbol;
      const labelSvgMarkup = String(sym?.svg_markup || sym?.svgMarkup || "").trim();
      const labelPngBase64 = String(sym?.png_preview_base64 || sym?.pngPreviewBase64 || "").trim();
      const hasRasterSymbol = Boolean(labelSvgMarkup || labelPngBase64);
      const fallbackLabelPosition =
        !labelPosition && anchor
          ? { x: anchor.x + 48 * markerScale, y: anchor.y + -40 * markerScale }
          : null;
      const effectiveLabel = labelPosition || fallbackLabelPosition;
      const baseSymbolSize = hasRasterSymbol ? 48 : 28;
      return {
        point: anchor,
        label: effectiveLabel,
        labelText: label,
        labelSvgMarkup,
        labelPngBase64,
        labelSymbolSize: baseSymbolSize * markerScale,
        labelSymbolGap: 10 * markerScale,
        pointClass: clsx("plan-marker-point", isSelected && "plan-marker-point--selected"),
        lineClass: "plan-marker-line",
        labelGroupClass: clsx("plan-marker-label-group", isSelected && "plan-marker-label-group--selected"),
        screenScale: 1,
        pointRadius: 4.5 * markerScale,
        pointTabIndex: anchor ? 0 : undefined,
        pointRole: anchor ? "button" : undefined,
        pointDataset: anchor ? { assetId: point.asset_id, kind: "object" } : null,
        labelTabIndex: effectiveLabel ? 0 : undefined,
        labelRole: effectiveLabel ? "button" : undefined,
        labelDataset: effectiveLabel ? { assetId: point.asset_id, kind: "label" } : null,
        labelPaddingX: 8,
        labelPaddingY: 4,
        labelRadiusX: 8,
        labelRadiusY: 8,
        labelFontSize: 12,
        labelStrokeWidth: 0,
        labelBadgeStrokeWidth: 1.5,
        title: label,
      };
    });

    renderPlanSvg({
      svg,
      image,
      layer,
      meta: metaRef.current,
      hint: hintRef.current,
      plan: activePlan,
      viewBox: `0 0 ${imageWidth} ${imageHeight}`,
      imageHref,
      imageWidth,
      imageHeight,
      markers,
      metaText: `${activePlan.source_file_type || "datei"} · Seite ${activePageNumber} · ${pagePoints.length} Marker · ${editModeDescription(editMode)}`,
      hintText: activePlan.rendered_plan_image_url
        ? `${selectedAssetId ? `Aktuelles Objekt: ${getAssetDefaultLabelText(selectedAsset) || selectedAssetId}.` : "Bitte zuerst ein Objekt auswählen."} ${editModeDescription(editMode)}.`
        : "Planvorschau ist für diese Datei aktuell nicht verfügbar.",
    });

    svg.classList.toggle("is-editing-object", editMode === "object" && Boolean(activePlan.rendered_plan_image_url && selectedAssetId));
    svg.classList.toggle("is-editing-label", editMode === "label" && Boolean(activePlan.rendered_plan_image_url && selectedAssetId));
    updateCanvasMetrics();
  }, [
    activePlan,
    activePageNumber,
    assets,
    editMode,
    filteredAssets,
    getSelectedPlanPoint,
    imageMetrics.height,
    imageMetrics.width,
    selectedAsset,
    selectedAssetId,
    selectedSubLocationId,
    updateCanvasMetrics,
  ]);

  useEffect(() => {
    renderOverlay();
  }, [renderOverlay]);

  useEffect(() => {
    const wrap = canvasWrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      updateCanvasMetrics();
      renderOverlay();
    });
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [renderOverlay, updateCanvasMetrics]);

  const planCoordsFromEvent = useCallback(
    (clientX: number, clientY: number) => {
      const svg = svgRef.current;
      const image = imageRef.current;
      if (!svg || !image) return null;
      const imageWidth = Math.max(1, imageMetrics.width);
      const imageHeight = Math.max(1, imageMetrics.height);
      return planCoordsFromClient(svg, clientX, clientY, {
        viewBoxX: 0,
        viewBoxY: 0,
        viewBoxWidth: imageWidth,
        viewBoxHeight: imageHeight,
        imageRect: {
          x: Number(image.getAttribute("x") || 0),
          y: Number(image.getAttribute("y") || 0),
          width: Number(image.getAttribute("width") || imageWidth),
          height: Number(image.getAttribute("height") || imageHeight),
          naturalWidth: imageWidth,
          naturalHeight: imageHeight,
          preserveAspectRatio: image.getAttribute("preserveAspectRatio") || "xMidYMid meet",
        },
      });
    },
    [imageMetrics.height, imageMetrics.width],
  );

  const savePlanPointUpdate = useCallback(
    async (payload: Record<string, unknown>) => {
      if (!activePlan?.id || !selectedAssetId || saveInFlight) return;
      setSaveInFlight(true);
      try {
        const result = await saveMaintenancePlanPoint(activePlan.id, selectedAssetId, payload);
        const saved = normalizePlanPointRecord(result.point as PlanPoint, activePlan);
        if (saved) onAssetPlanUpdated(selectedAssetId, saved, activePlan);
        renderOverlay();
      } catch (err) {
        onFeedback(err instanceof ApiError ? err.message : "Planpunkt konnte nicht gespeichert werden.", "error");
      } finally {
        setSaveInFlight(false);
      }
    },
    [activePlan, onAssetPlanUpdated, onFeedback, renderOverlay, saveInFlight, selectedAssetId],
  );

  const savePlanPoint = useCallback(
    async (mode: "object" | "label", coords: { x: number; y: number }) => {
      if (!activePlan?.id || !selectedAssetId) return;
      const existing = getSelectedPlanPoint(activePlan, selectedAssetId) || {};
      const defaultLabel = getAssetDefaultLabelText(selectedAsset);
      await savePlanPointUpdate({
        asset_id: selectedAssetId,
        x: mode === "object" ? coords.x : existing.x ?? null,
        y: mode === "object" ? coords.y : existing.y ?? null,
        label_x: mode === "label" ? coords.x : existing.label_x ?? null,
        label_y: mode === "label" ? coords.y : existing.label_y ?? null,
        label_text: labelText.trim() || defaultLabel,
        coordinate_mode: "normalized",
        page_number: activePageNumber,
      });
    },
    [activePlan, activePageNumber, getSelectedPlanPoint, labelText, savePlanPointUpdate, selectedAsset, selectedAssetId],
  );

  const eventTargetAssetId = (target: EventTarget | null) => {
    const raw = (target as HTMLElement | null)?.closest?.("[data-asset-id]")?.getAttribute("data-asset-id");
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const eventTargetKind = (target: EventTarget | null): "object" | "label" | "" => {
    const raw = (target as HTMLElement | null)?.closest?.("[data-kind]")?.getAttribute("data-kind");
    return raw === "label" || raw === "object" ? raw : "";
  };

  const pointerShouldEdit = () =>
    Boolean(activePlan?.rendered_plan_image_url && selectedAssetId && editMode !== "display");

  const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    const assetIdFromTarget = eventTargetAssetId(event.target);
    if (assetIdFromTarget && editMode !== "display") onSelectAsset(assetIdFromTarget);

    if (editMode === "display" && event.button === 0) {
      const wrap = canvasWrapRef.current;
      if (wrap) {
        const canPanH = wrap.scrollWidth - wrap.clientWidth > 1;
        const canPanV = wrap.scrollHeight - wrap.clientHeight > 1;
        if (canPanH || canPanV) {
          panRef.current = {
            pointerId: event.pointerId,
            startClientX: event.clientX,
            startClientY: event.clientY,
            startScrollLeft: wrap.scrollLeft,
            startScrollTop: wrap.scrollTop,
            moved: false,
          };
          wrap.classList.add("is-panning");
          // Capture auf dem SVG (Handler-Element), nicht auf dem Wrapper —
          // sonst kommen pointerup/move nicht an und der Drag bleibt hängen.
          event.currentTarget.setPointerCapture(event.pointerId);
          event.preventDefault();
          return;
        }
      }
    }

    if (!pointerShouldEdit()) return;
    const coords = planCoordsFromEvent(event.clientX, event.clientY);
    if (!coords) return;
    const kindFromTarget = eventTargetKind(event.target);
    const dragKind = kindFromTarget === "label" || kindFromTarget === "object" ? kindFromTarget : editMode;
    if (dragKind !== "object" && dragKind !== "label") return;
    dragRef.current = { pointerId: event.pointerId, kind: dragKind };
    svgRef.current?.setPointerCapture(event.pointerId);
    event.preventDefault();
    void savePlanPoint(dragKind, coords);
  };

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (panRef.current && panRef.current.pointerId === event.pointerId && canvasWrapRef.current) {
      const dx = event.clientX - panRef.current.startClientX;
      const dy = event.clientY - panRef.current.startClientY;
      if (!panRef.current.moved && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) panRef.current.moved = true;
      canvasWrapRef.current.scrollLeft = panRef.current.startScrollLeft - dx;
      canvasWrapRef.current.scrollTop = panRef.current.startScrollTop - dy;
      event.preventDefault();
      return;
    }
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    const coords = planCoordsFromEvent(event.clientX, event.clientY);
    if (!coords) return;
    event.preventDefault();
    void savePlanPoint(dragRef.current.kind, coords);
  };

  const endPointer = (event: React.PointerEvent<SVGSVGElement>) => {
    if (panRef.current && (!event || panRef.current.pointerId === event.pointerId)) {
      try {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      } catch {
        /* ignore */
      }
      if (panRef.current.moved) suppressClickUntilRef.current = Date.now() + 250;
      canvasWrapRef.current?.classList.remove("is-panning");
      panRef.current = null;
    }
    if (!dragRef.current || (event && dragRef.current.pointerId !== event.pointerId)) return;
    try {
      svgRef.current?.releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
    suppressClickUntilRef.current = Date.now() + 250;
    dragRef.current = null;
  };

  const handleSvgClick = (event: React.MouseEvent<SVGSVGElement>) => {
    if (dragRef.current || Date.now() < suppressClickUntilRef.current) return;
    const assetId = eventTargetAssetId(event.target);
    if (editMode === "display") {
      if (assetId && assetId === selectedAssetId) {
        onSelectAsset(null);
        return;
      }
      if (assetId) {
        onSelectAsset(assetId);
        return;
      }
      if (selectedAssetId) onSelectAsset(null);
      return;
    }
    if (!pointerShouldEdit()) return;
    const coords = planCoordsFromEvent(event.clientX, event.clientY);
    if (!coords) return;
    const kindFromTarget = eventTargetKind(event.target);
    const mode = kindFromTarget === "label" || kindFromTarget === "object" ? kindFromTarget : editMode;
    if (mode === "object" || mode === "label") void savePlanPoint(mode, coords);
  };

  async function handleSaveLabelText() {
    if (!activePlan?.id || !selectedAssetId) return;
    const existing = getSelectedPlanPoint(activePlan, selectedAssetId) || {};
    await savePlanPointUpdate({
      asset_id: selectedAssetId,
      x: existing.x ?? null,
      y: existing.y ?? null,
      label_x: existing.label_x ?? null,
      label_y: existing.label_y ?? null,
      label_text: labelText.trim() || getAssetDefaultLabelText(selectedAsset),
      coordinate_mode: "normalized",
      page_number: activePageNumber,
    });
    onFeedback("Labeltext wurde gespeichert.", "success");
  }

  async function handleClearPoints() {
    if (!activePlan?.id || !selectedAssetId) return;
    await savePlanPointUpdate({
      asset_id: selectedAssetId,
      x: null,
      y: null,
      label_x: null,
      label_y: null,
      label_text: labelText.trim() || getAssetDefaultLabelText(selectedAsset),
      coordinate_mode: "normalized",
      page_number: activePageNumber,
    });
  }

  const isPdf = String(activePlan?.source_file_type || "").includes("pdf");

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-[180px] flex-1 space-y-1">
          <span className="eh-label text-xs">Unterort-Plan</span>
          <select
            className="eh-input w-full"
            value={activePlanId ?? ""}
            onChange={(e) => onActivePlanIdChange(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Kein Plan gewählt</option>
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id ?? ""}>
                {plan.title || plan.filename || `Plan ${plan.id}`}
                {plan.is_active ? " (aktiv)" : ""}
              </option>
            ))}
          </select>
        </label>
        {isPdf ? (
          <label className="space-y-1">
            <span className="eh-label text-xs">Seite</span>
            <select className="eh-input" value={activePageNumber} onChange={(e) => setActivePageNumber(Number(e.target.value))}>
              {pageOptions.map((page) => (
                <option key={page} value={page}>
                  Seite {page}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <span ref={metaRef} className="text-xs text-label-secondary" />
        <Button type="button" variant="secondary" size="sm" onClick={() => setEditPanelOpen((v) => !v)}>
          {editPanelOpen ? "Bearbeitung schließen" : "Bearbeiten"}
        </Button>
      </div>

      {editPanelOpen ? (
        <div className="space-y-3 rounded-ios border border-separator bg-card-elevated p-3">
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl
              name="Bearbeitungsmodus"
              value={editMode}
              onChange={setEditMode}
              options={[
                { value: "display", label: "Anzeige" },
                { value: "object", label: "Punkt setzen" },
                { value: "label", label: "Label setzen" },
              ]}
            />
            <Button type="button" variant="secondary" size="sm" disabled={!selectedAssetId} onClick={() => void handleClearPoints()}>
              Positionen leeren
            </Button>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            <label className="space-y-1">
              <span className="eh-label text-xs">Objekt</span>
              <input className="eh-input w-full" placeholder="Suchen …" value={assetSearch} onChange={(e) => setAssetSearch(e.target.value)} />
              <select
                className="eh-input w-full"
                value={selectedAssetId ?? ""}
                onChange={(e) => onSelectAsset(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Objekt wählen</option>
                {assetOptions.map((asset) => (
                  <option key={asset.asset_id} value={asset.asset_id}>
                    {[asset.external_id || "Ohne Kennung", asset.name || "Ohne Namen", asset.location_label || "Basisort"].join(" · ")}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="eh-label text-xs">Label</span>
              <div className="flex gap-2">
                <input className="eh-input w-full" value={labelText} disabled={!selectedAssetId} onChange={(e) => setLabelText(e.target.value)} />
                <Button type="button" variant="secondary" size="sm" disabled={!selectedAssetId} onClick={() => void handleSaveLabelText()}>
                  Speichern
                </Button>
              </div>
              <span className="text-xs text-label-tertiary">
                {getAssetDefaultLabelText(selectedAsset)
                  ? `Standard: ${getAssetDefaultLabelText(selectedAsset)}`
                  : "Kein Standardlabel verfügbar"}
              </span>
            </label>
          </div>
        </div>
      ) : null}

      <p className="text-sm text-label-secondary">
        {!selectedSubLocationId
          ? "Bitte einen Unterort auswählen."
          : "Planansicht aktiv. Objekte können auf dem Plan positioniert werden."}
      </p>

      <div className="huelle-plan-stage">
        <div ref={canvasWrapRef} className="bs-plan-canvas-wrap">
          <svg
            ref={svgRef}
            className="bs-plan-svg"
            viewBox="0 0 1 1"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Unterort-Plan mit Asset-Markern"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endPointer}
            onPointerCancel={endPointer}
            onLostPointerCapture={endPointer}
            onClick={handleSvgClick}
          >
            <image ref={imageRef} className="bs-plan-image" href="" x="0" y="0" width="1" height="1" />
            <g ref={layerRef} />
          </svg>
        </div>
        <div className="huelle-plan-zoom-controls" aria-label="Plan-Zoom">
          <Button type="button" variant="secondary" size="sm" onClick={() => setPlanZoom((z) => Math.max(PLAN_ZOOM_MIN, z - PLAN_ZOOM_STEP))}>
            −
          </Button>
          <span className="px-1 text-xs">{Math.round(planZoom * 100)}%</span>
          <Button type="button" variant="secondary" size="sm" onClick={() => setPlanZoom((z) => Math.min(PLAN_ZOOM_MAX, z + PLAN_ZOOM_STEP))}>
            +
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => setPlanZoom(1)}>
            Reset
          </Button>
        </div>
      </div>
      <p ref={hintRef} className="text-sm text-label-secondary" />
    </div>
  );
}

function editModeDescription(mode: PlanEditMode): string {
  if (mode === "object") return "Klick oder Ziehen setzt/verschiebt den Türpunkt";
  if (mode === "label") return "Klick oder Ziehen setzt/verschiebt die Labelposition";
  return "Anzeige aktiv";
}
